import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
	AgGridAtom,
	DropdownAtom,
	GuidanceCallout,
	ModalAtom,
	TextFieldAtom,
} from "../../../components/atoms/atom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import {
	createDefaultPermissions,
	normalizePermissions,
	PERMISSION_AREA_CONFIG,
	TICKET_ACCESS_LEVEL,
} from "../../../constant/projectPermissions";
import { USER_ROLES } from "../../../constant/roles";
import {
	useCreateSystemTemplateMutation,
	useDeleteSystemTemplateMutation,
	useListSystemTemplatesQuery,
	useUpdateSystemTemplateMutation,
} from "../../../store/services/adminPermissionTemplates";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import { isGlobalAdminRole } from "../../../utils/isGlobalAdmin";
import { getFormattedDate, showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import PermissionsForm from "../../projects/_shared/permissions-form";
import "./permission-templates.scss";

const SEARCH_FIELDS = ["name", "description"];

function getEnabledPermissionAreas(permissions) {
	if (!permissions || typeof permissions !== "object") {
		return [];
	}
	return PERMISSION_AREA_CONFIG.filter((config) => {
		const area = permissions[config.area];
		if (!area) return false;
		if (config.ticketLevels) {
			return area.view && area.view !== TICKET_ACCESS_LEVEL.NONE;
		}
		return area.view === true;
	}).map((config) => config.area);
}

const PermissionAreasSummary = ({ permissions, t }) => {
	const areas = getEnabledPermissionAreas(permissions);
	if (areas.length === 0) {
		return (
			<span className="admin-permission-templates__no-areas text-muted">
				{t("admin.permission-templates.no-areas")}
			</span>
		);
	}
	return (
		<div className="admin-permission-templates__badges">
			{areas.map((areaKey) => {
				const config = PERMISSION_AREA_CONFIG.find(
					(c) => c.area === areaKey,
				);
				return (
					<Badge
						key={areaKey}
						bg="light"
						text="dark"
						className="admin-permission-templates__area-badge"
					>
						{config ? t(config.labelKey) : areaKey}
					</Badge>
				);
			})}
		</div>
	);
};

const getTemplateColumns = (t, handlers, { isSuperAdmin, isGlobalAdmin }) => [
	{
		field: "createdAt",
		headerName: t("projects.provided"),
		maxWidth: 110,
		valueFormatter: (params) => getFormattedDate(params.value),
	},
	{
		field: "name",
		headerName: t("projects.permissions.preset-name"),
		minWidth: 180,
		flex: 1,
		unSortIcon: true,
	},
	{
		field: "description",
		headerName: t("projects.permissions.preset-description"),
		minWidth: 200,
		flex: 1.2,
		unSortIcon: true,
		valueGetter: (params) => params.data?.description?.trim() || "-",
	},
	{
		field: "permissions",
		headerName: t("admin.permission-templates.areas-column"),
		minWidth: 260,
		flex: 1.5,
		sortable: false,
		filter: false,
		autoHeight: true,
		cellRenderer: (params) => (
			<PermissionAreasSummary
				permissions={params.data?.permissions}
				t={t}
			/>
		),
	},
	createActionColumn({
		t,
		maxWidth: 100,
		actions: [
			{
				onClick: handlers.onEdit,
				label: "common.edit",
				show: () => isGlobalAdmin,
			},
			{
				onClick: handlers.onDelete,
				label: "admin.permission-templates.delete",
				danger: true,
				show: () => isSuperAdmin,
			},
		],
	}),
];

function PermissionTemplatesAdminPage() {
	const { t } = useTranslation();
	const userRole = useSelector((state) => state.auth.user?.role);
	const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
	const isGlobalAdmin = isGlobalAdminRole(userRole);

	const { data, isLoading, isFetching } = useListSystemTemplatesQuery();
	const [createTemplate, { isLoading: isCreating }] =
		useCreateSystemTemplateMutation();
	const [updateTemplate, { isLoading: isUpdating }] =
		useUpdateSystemTemplateMutation();
	const [deleteTemplate, { isLoading: isDeleting }] =
		useDeleteSystemTemplateMutation();

	const templates = useMemo(() => data?.items ?? [], [data?.items]);
	const saving = isCreating || isUpdating;

	const filterConfiguration = useMemo(
		() =>
			getSearchAddFilter({
				addTextKey: "admin.permission-templates.add",
				hideAddButton: !isGlobalAdmin,
			}),
		[isGlobalAdmin],
	);
	const [filterItems, setFilterItems] = useState(filterConfiguration);
	const [filteredTemplates, setFilteredTemplates] = useState([]);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [editName, setEditName] = useState("");
	const [editDescription, setEditDescription] = useState("");
	const [editPermissions, setEditPermissions] = useState(null);
	const [copyFromId, setCopyFromId] = useState("");
	const [templateToDelete, setTemplateToDelete] = useState(null);

	const copyFromOptions = useMemo(
		() =>
			templates.map((template) => ({
				value: template.id,
				label: template.name,
			})),
		[templates],
	);

	const searchColumnDefs = useMemo(
		() => SEARCH_FIELDS.map((field) => ({ field })),
		[],
	);

	useEffect(() => {
		setFilterItems(filterConfiguration);
	}, [filterConfiguration]);

	useEffect(() => {
		setFilteredTemplates(templates);
	}, [templates]);

	const openEditModal = useCallback((template) => {
		setEditingTemplate(template);
		setEditName(template.name || "");
		setEditDescription(template.description || "");
		setEditPermissions(
			normalizePermissions(
				template.permissions || createDefaultPermissions(),
			),
		);
		setCopyFromId("");
		setShowEditModal(true);
	}, []);

	const openCreateModal = useCallback(() => {
		openEditModal({
			id: null,
			name: "",
			description: "",
			permissions: createDefaultPermissions(),
		});
	}, [openEditModal]);

	const handleCopyFromChange = useCallback(
		(templateId) => {
			setCopyFromId(templateId);
			if (!templateId) {
				setEditPermissions(createDefaultPermissions());
				return;
			}
			const source = templates.find((item) => item.id === templateId);
			if (source?.permissions) {
				setEditPermissions(
					normalizePermissions(source.permissions),
				);
			}
		},
		[templates],
	);

	const handleSave = async () => {
		if (!editName.trim()) {
			showToast(t("projects.permissions.error-name-required"), {
				type: "error",
			});
			return;
		}
		try {
			const payload = {
				name: editName.trim(),
				description: editDescription.trim() || null,
				permissions: editPermissions,
			};
			if (editingTemplate?.id) {
				await updateTemplate({
					id: editingTemplate.id,
					...payload,
				}).unwrap();
				showToast(t("admin.permission-templates.updated-success"), {
					type: "success",
				});
			} else {
				await createTemplate(payload).unwrap();
				showToast(t("admin.permission-templates.created-success"), {
					type: "success",
				});
			}
			setShowEditModal(false);
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const handleDelete = async () => {
		if (!templateToDelete) return;
		try {
			await deleteTemplate(templateToDelete.id).unwrap();
			showToast(t("admin.permission-templates.deleted-success"), {
				type: "success",
			});
			setShowDeleteModal(false);
			setTemplateToDelete(null);
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const handleEdit = useCallback(
		(row) => {
			openEditModal(row);
		},
		[openEditModal],
	);

	const handleDeleteClick = useCallback(
		(row) => {
			if (!isSuperAdmin) return;
			setTemplateToDelete(row);
			setShowDeleteModal(true);
		},
		[isSuperAdmin],
	);

	const actionHandlers = useMemo(
		() => ({
			onEdit: handleEdit,
			onDelete: handleDeleteClick,
		}),
		[handleEdit, handleDeleteClick],
	);

	const columns = useMemo(
		() =>
			getTemplateColumns(t, actionHandlers, { isSuperAdmin, isGlobalAdmin }),
		[t, actionHandlers, isSuperAdmin, isGlobalAdmin],
	);

	const onFilterAction = (index, key, info) => {
		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value;
		setFilterItems(localFilterItems);

		let filtered = [...templates];
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(
					item.value,
					filtered,
					searchColumnDefs,
				);
			}
		});
		setFilteredTemplates(filtered);

		if (key === "add") {
			openCreateModal();
		}
	};

	const isNewTemplate = !editingTemplate?.id;
	const loading = isLoading || isFetching;
	const isEmpty = !loading && templates.length === 0;

	return (
		<div className="admin-page admin-permission-templates">
			<header className="admin-permission-templates__header">
				<h1 className="admin-page__title mb-1">
					{t("admin.permission-templates.title")}
				</h1>
				<p className="admin-permission-templates__subtitle text-muted mb-0">
					{t("admin.permission-templates.subtitle")}
				</p>
			</header>

			<GuidanceCallout
				variant="info"
				title={t("admin.permission-templates.intro-title")}
				message={t("admin.permission-templates.intro-message")}
				steps={[
					t("admin.permission-templates.intro-step-1"),
					t("admin.permission-templates.intro-step-2"),
				]}
			/>

			<div className="page-container">
				{isEmpty ? (
					<GuidanceCallout
						variant="action"
						title={t("admin.permission-templates.empty-title")}
						message={t("admin.permission-templates.empty-message")}
						actions={
							isGlobalAdmin
								? [
										{
											label: t("admin.permission-templates.add"),
											onClick: openCreateModal,
											variant: "primary",
										},
									]
								: []
						}
					/>
				) : (
					<>
						<FilterBar
							items={filterItems}
							onFilterAction={onFilterAction}
						/>

						<div className="flex-grow-1 ag-grid-parent">
							{loading && filteredTemplates.length === 0 ? (
								<TableSkeleton />
							) : (
								<AgGridAtom
									columnDefs={columns}
									rowData={filteredTemplates}
									overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("projects.permissions.no-presets")}</span>`}
									defaultColDef={{
										flex: 1,
										sortable: true,
										filter: false,
									}}
								/>
							)}
						</div>
					</>
				)}

				<ModalAtom
					show={showEditModal}
					handleClose={() => setShowEditModal(false)}
					customClassName="admin-permission-templates__modal"
					title={
						isNewTemplate
							? t("admin.permission-templates.add")
							: t("admin.permission-templates.edit")
					}
					titleHelpText={t(
						"admin.permission-templates.section-permissions-hint",
					)}
					saveText={saving ? "common.saving" : "common.save"}
					disabled={saving}
					handleSubmit={handleSave}
					body={
						editPermissions && (
							<div className="admin-permission-templates__modal-body">
								<section className="admin-permission-templates__section">
									<h3 className="admin-permission-templates__section-title">
										{t(
											"admin.permission-templates.section-details",
										)}
									</h3>
									<div className="row g-3 mx-0">
										<div className="col-md-6">
											<TextFieldAtom
												label={t(
													"projects.permissions.preset-name",
												)}
												maxLength={50}
												value={editName}
												onChange={(e) =>
													setEditName(e.target.value)
												}
											/>
										</div>
										<div className="col-md-6">
											<TextFieldAtom
												label={t(
													"projects.permissions.preset-description",
												)}
												maxLength={255}
												value={editDescription}
												onChange={(e) =>
													setEditDescription(
														e.target.value,
													)
												}
											/>
										</div>
									</div>
									{isNewTemplate && copyFromOptions.length > 0 && (
										<div className="mt-3">
											<DropdownAtom
												label={t(
													"admin.permission-templates.copy-from",
												)}
												placeholder={t(
													"admin.permission-templates.copy-from-placeholder",
												)}
												value={copyFromId}
												options={copyFromOptions}
												onChange={(e) =>
													handleCopyFromChange(
														e.target.value,
													)
												}
												isSearchable
												isClearable
												sortOptions={false}
											/>
										</div>
									)}
								</section>

								<section className="admin-permission-templates__section">
									<h3 className="admin-permission-templates__section-title">
										{t(
											"admin.permission-templates.section-permissions",
										)}
									</h3>
									<p className="admin-permission-templates__section-hint text-muted">
										{t(
											"admin.permission-templates.section-permissions-hint",
										)}
									</p>
									<PermissionsForm
										permissions={editPermissions}
										onChange={setEditPermissions}
										showCategoryAccess={false}
									/>
								</section>
							</div>
						)
					}
				/>

				<ModalAtom
					show={showDeleteModal}
					size="sm"
					handleClose={() => setShowDeleteModal(false)}
					title={t("admin.permission-templates.delete")}
					saveText={isDeleting ? "common.saving" : "common.delete"}
					disabled={isDeleting}
					handleSubmit={handleDelete}
					body={
						<p>
							{t("admin.permission-templates.delete-confirm")}
							{templateToDelete?.name ? (
								<>
									{" "}
									<strong>{templateToDelete.name}</strong>
								</>
							) : null}
						</p>
					}
				/>
			</div>
		</div>
	);
}

export default PermissionTemplatesAdminPage;
